import { useCallback, useEffect, useRef, useState } from "react"

export type AudioRecorderPhase = "idle" | "countdown" | "recording"

export type AudioRecorderSource =
  | {
      kind: "microphone"
      deviceId?: string | null
      /**
       * Устройство подтверждено текущим списком — ограничение жёсткое (браузер обязан
       * его выполнить). Для непроверенного id (например, восстановленного из памяти
       * до выдачи прав) — мягкое: протухший id иначе валит запись OverconstrainedError.
       */
      exact?: boolean
    }
  | { kind: "system" }

export type UseAudioRecorderOptions = {
  minSeconds: number
  maxSeconds: number
  countdownSeconds?: number
  /** Источник записи: микрофон (опционально конкретный deviceId) или системный звук (getDisplayMedia). */
  source?: AudioRecorderSource
  onRecorded: (file: File) => void
  onTooShort?: (durationSeconds: number) => void
  /** Захват не начался: отказ в правах или закрытый диалог шаринга. */
  onPermissionDenied?: (source: "microphone" | "system") => void
  onError?: (message: string) => void
  /**
   * Захват стартовал: только теперь браузер раскрывает имена и id устройств.
   * capturedDeviceId — устройство, с которого реально идёт запись (может отличаться
   * от запрошенного: у Chrome есть свой выбор микрофона для сайта).
   */
  onCaptureStarted?: (capturedDeviceId: string | null) => void
  /** Выбранного устройства больше нет — записываем со входа по умолчанию. */
  onDeviceUnavailable?: () => void
  /**
   * Уточняет устройство сразу после выдачи прав: список с именами и id браузер
   * раскрывает только теперь, и сохранённый id мог протухнуть (Safari меняет его на
   * каждую загрузку). Возвращает deviceId, на который надо переключиться, или null.
   */
  resolveCapturedDevice?: (devices: MediaDeviceInfo[], capturedDeviceId: string | null) => string | null
  /** Системный звук выбран, но в диалоге шаринга не включили «поделиться звуком». */
  onSystemAudioMissing?: () => void
  /**
   * MediaRecorder уже принял start(). Синхронно, до ближайшего кадра: так видео
   * для переозвучки стартует вместе с записью, а не после перерисовки.
   */
  onRecordingStarted?: () => void
}

/** Пользователь не включил звук в диалоге шаринга — это не отказ в правах. */
class SystemAudioMissingError extends Error {}

/**
 * Системный звук снимается через getDisplayMedia: спека требует видеотрек даже когда
 * нужен только звук, поэтому его сразу останавливаем. Аудио тут отдают лишь Chromium-браузеры.
 */
async function getSystemAudioStream(): Promise<MediaStream> {
  const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: true })
  display.getVideoTracks().forEach((track) => track.stop())
  const audioTracks = display.getAudioTracks()
  if (audioTracks.length === 0) {
    display.getTracks().forEach((track) => track.stop())
    throw new SystemAudioMissingError("System audio capture is not available")
  }
  return new MediaStream(audioTracks)
}

function pickMimeType(): string | undefined {
  if (typeof MediaRecorder === "undefined") return undefined
  const candidates = ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]
  return candidates.find((type) => MediaRecorder.isTypeSupported(type))
}

/** Strip codec params so uploads match server allowlists (`audio/webm`, not `audio/webm;codecs=opus`). */
function baseMimeType(mime: string): string {
  const base = mime.split(";", 1)[0]?.trim().toLowerCase()
  return base || "audio/webm"
}

function extensionForMime(mime: string): string {
  const base = baseMimeType(mime)
  if (base.includes("mp4") || base.includes("m4a")) return "m4a"
  if (base.includes("ogg")) return "ogg"
  return "webm"
}

function buildRecordingFileName(mime: string): string {
  const now = new Date()
  const pad = (n: number) => String(n).padStart(2, "0")
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    "-",
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join("")
  return `voice-recording-${stamp}.${extensionForMime(mime)}`
}

export function useAudioRecorder({
  minSeconds,
  maxSeconds,
  countdownSeconds = 3,
  source,
  onRecorded,
  onTooShort,
  onPermissionDenied,
  onError,
  onCaptureStarted,
  onDeviceUnavailable,
  onSystemAudioMissing,
  resolveCapturedDevice,
  onRecordingStarted,
}: UseAudioRecorderOptions) {
  const [phase, setPhase] = useState<AudioRecorderPhase>("idle")
  const [countdownLeft, setCountdownLeft] = useState(0)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  /** phase ещё idle, а getUserMedia уже идёт — селектор входа в это время тоже занят. */
  const [starting, setStarting] = useState(false)
  /** Поток только пока phase === "recording": метр громкости читает его, не открывая микрофон ещё раз. */
  const [stream, setStream] = useState<MediaStream | null>(null)

  const streamRef = useRef<MediaStream | null>(null)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const startedAtRef = useRef(0)
  const elapsedRef = useRef(0)
  const acceptRecordingRef = useRef(true)
  const startingRef = useRef(false)
  /**
   * Поколение попытки старта. Захват идёт через несколько await (права, перечисление
   * устройств, переоткрытие потока); форму за это время могут закрыть, а cancel() в фазе
   * idle ещё ничего не остановит. Смена токена = попытку бросили: поток гасим и выходим.
   */
  const startTokenRef = useRef(0)
  const tickTimerRef = useRef<number | null>(null)
  const countdownTimerRef = useRef<number | null>(null)
  const maxStopTimerRef = useRef<number | null>(null)
  const goTimerRef = useRef<number | null>(null)
  const minSecondsRef = useRef(minSeconds)
  const maxSecondsRef = useRef(maxSeconds)
  const sourceRef = useRef(source)
  const onRecordedRef = useRef(onRecorded)
  const onTooShortRef = useRef(onTooShort)
  const onPermissionDeniedRef = useRef(onPermissionDenied)
  const onErrorRef = useRef(onError)
  const onCaptureStartedRef = useRef(onCaptureStarted)
  const onDeviceUnavailableRef = useRef(onDeviceUnavailable)
  const onSystemAudioMissingRef = useRef(onSystemAudioMissing)
  const resolveCapturedDeviceRef = useRef(resolveCapturedDevice)
  const onRecordingStartedRef = useRef(onRecordingStarted)

  minSecondsRef.current = minSeconds
  maxSecondsRef.current = maxSeconds
  sourceRef.current = source
  onRecordedRef.current = onRecorded
  onTooShortRef.current = onTooShort
  onPermissionDeniedRef.current = onPermissionDenied
  onErrorRef.current = onError
  onCaptureStartedRef.current = onCaptureStarted
  onDeviceUnavailableRef.current = onDeviceUnavailable
  onSystemAudioMissingRef.current = onSystemAudioMissing
  resolveCapturedDeviceRef.current = resolveCapturedDevice
  onRecordingStartedRef.current = onRecordingStarted

  const clearTimers = useCallback(() => {
    if (tickTimerRef.current != null) {
      window.clearInterval(tickTimerRef.current)
      tickTimerRef.current = null
    }
    if (countdownTimerRef.current != null) {
      window.clearInterval(countdownTimerRef.current)
      countdownTimerRef.current = null
    }
    if (maxStopTimerRef.current != null) {
      window.clearTimeout(maxStopTimerRef.current)
      maxStopTimerRef.current = null
    }
    if (goTimerRef.current != null) {
      window.clearTimeout(goTimerRef.current)
      goTimerRef.current = null
    }
  }, [])

  const stopStream = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
    setStream(null)
  }, [])

  const resetToIdle = useCallback(() => {
    startTokenRef.current += 1
    clearTimers()
    stopStream()
    recorderRef.current = null
    chunksRef.current = []
    startedAtRef.current = 0
    elapsedRef.current = 0
    startingRef.current = false
    setStarting(false)
    setElapsedSeconds(0)
    setCountdownLeft(0)
    setPhase("idle")
  }, [clearTimers, stopStream])

  const finishRecording = useCallback(
    (accept: boolean) => {
      const recorder = recorderRef.current
      if (!recorder || recorder.state === "inactive") {
        resetToIdle()
        return
      }
      if (maxStopTimerRef.current != null) {
        window.clearTimeout(maxStopTimerRef.current)
        maxStopTimerRef.current = null
      }
      if (tickTimerRef.current != null) {
        window.clearInterval(tickTimerRef.current)
        tickTimerRef.current = null
      }
      if (startedAtRef.current > 0) {
        elapsedRef.current = (performance.now() - startedAtRef.current) / 1000
        setElapsedSeconds(Math.floor(elapsedRef.current))
      }
      acceptRecordingRef.current = accept
      try {
        recorder.stop()
      } catch {
        resetToIdle()
      }
    },
    [resetToIdle],
  )

  const beginRecording = useCallback(
    (stream: MediaStream) => {
      let effectiveMime = pickMimeType()
      const abortStart = (e: unknown) => {
        stopStream()
        recorderRef.current = null
        chunksRef.current = []
        startingRef.current = false
        setStarting(false)
        setPhase("idle")
        onErrorRef.current?.(e instanceof Error ? e.message : String(e))
      }

      let recorder: MediaRecorder
      try {
        recorder = effectiveMime ? new MediaRecorder(stream, { mimeType: effectiveMime }) : new MediaRecorder(stream)
      } catch (first) {
        // iOS Safari: isTypeSupported() отвечает за формат, но не за пару
        // «конструктор + start» — пробуем ещё раз без явного mimeType.
        try {
          recorder = new MediaRecorder(stream)
          effectiveMime = undefined
        } catch {
          abortStart(first)
          return
        }
      }

      chunksRef.current = []
      acceptRecordingRef.current = true
      recorderRef.current = recorder

      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) chunksRef.current.push(event.data)
      }

      recorder.onstop = () => {
        clearTimers()
        const duration = elapsedRef.current
        const mime = baseMimeType(recorder.mimeType || effectiveMime || "audio/webm")
        const chunks = chunksRef.current
        const shouldAccept = acceptRecordingRef.current
        stopStream()
        recorderRef.current = null
        chunksRef.current = []
        startingRef.current = false
        setStarting(false)
        setPhase("idle")
        setElapsedSeconds(0)
        setCountdownLeft(0)

        if (!shouldAccept) return

        if (duration < minSecondsRef.current) {
          onTooShortRef.current?.(duration)
          return
        }

        const blob = new Blob(chunks, { type: mime })
        if (blob.size === 0) {
          onErrorRef.current?.("Empty recording")
          return
        }

        const file = new File([blob], buildRecordingFileName(mime), {
          type: mime,
          lastModified: Date.now(),
        })
        onRecordedRef.current(file)
      }

      try {
        recorder.start(250)
      } catch (e) {
        // onstop уже не сработает — убираем состояние сами, иначе фаза зависнет.
        abortStart(e)
        return
      }
      onRecordingStartedRef.current?.()
      startedAtRef.current = performance.now()
      elapsedRef.current = 0
      setElapsedSeconds(0)
      setPhase("recording")
      setStream(stream)

      tickTimerRef.current = window.setInterval(() => {
        const next = (performance.now() - startedAtRef.current) / 1000
        elapsedRef.current = next
        setElapsedSeconds(Math.floor(next))
      }, 100)

      maxStopTimerRef.current = window.setTimeout(
        () => {
          finishRecording(true)
        },
        Math.max(0, maxSecondsRef.current * 1000),
      )
    },
    [clearTimers, finishRecording, stopStream],
  )

  const startCountdown = useCallback(async () => {
    if (phase !== "idle" || startingRef.current) return
    const src = sourceRef.current
    const wantsSystemAudio = src?.kind === "system"
    if (typeof navigator === "undefined") return
    if (wantsSystemAudio ? !navigator.mediaDevices?.getDisplayMedia : !navigator.mediaDevices?.getUserMedia) {
      onErrorRef.current?.(
        wantsSystemAudio
          ? "System audio capture is not supported in this browser"
          : "Microphone recording is not supported in this browser",
      )
      return
    }

    startingRef.current = true
    setStarting(true)
    const token = (startTokenRef.current += 1)
    const aborted = () => startTokenRef.current !== token
    let stream: MediaStream
    /** Устройство уже не удалось получить — второй раз об этом не сообщаем. */
    let deviceFellBack = false
    try {
      if (wantsSystemAudio) {
        stream = await getSystemAudioStream()
      } else {
        const deviceId = src?.kind === "microphone" ? src.deviceId : null
        // exact: явный выбор пользователя обязателен к исполнению. С ideal браузер
        // вправе отдать другое устройство (у Chrome свой рейтинг микрофонов), и запись
        // молча пошла бы не с того входа. Неподтверждённый id идёт мягким ограничением.
        const exact = src?.kind === "microphone" && src.exact === true
        try {
          stream = await navigator.mediaDevices.getUserMedia({
            audio: deviceId ? { deviceId: exact ? { exact: deviceId } : { ideal: deviceId } } : true,
          })
        } catch (error) {
          // Устройства больше нет — записываем со входа по умолчанию, но говорим об этом.
          // Сверяем по name: OverconstrainedError — это DOMException, не отдельный класс.
          if (deviceId && (error as { name?: string })?.name === "OverconstrainedError") {
            deviceFellBack = true
            onDeviceUnavailableRef.current?.()
            stream = await navigator.mediaDevices.getUserMedia({ audio: true })
          } else {
            throw error
          }
        }
      }
    } catch (error) {
      startingRef.current = false
      setStarting(false)
      // Пустой аудиопоток из диалога шаринга — не отказ в правах, сообщение должно отличаться.
      if (error instanceof SystemAudioMissingError) onSystemAudioMissingRef.current?.()
      else onPermissionDeniedRef.current?.(wantsSystemAudio ? "system" : "microphone")
      return
    }

    if (aborted()) {
      stream.getTracks().forEach((track) => track.stop())
      startingRef.current = false
      setStarting(false)
      return
    }

    // Мягкое ограничение браузер вправе проигнорировать и отдать свой предпочитаемый
    // микрофон. Права уже выданы, список раскрыт — уточняем устройство и переоткрываем
    // поток, пока идёт обратный отсчёт: иначе дубль молча запишется не с того входа.
    if (!wantsSystemAudio && !deviceFellBack && resolveCapturedDeviceRef.current) {
      const captured = stream.getAudioTracks()[0]?.getSettings().deviceId ?? null
      const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[])
      const switchTo = aborted() ? null : resolveCapturedDeviceRef.current(devices, captured)
      if (switchTo && switchTo !== captured) {
        try {
          const better = await navigator.mediaDevices.getUserMedia({ audio: { deviceId: { exact: switchTo } } })
          stream.getTracks().forEach((track) => track.stop())
          stream = better
        } catch {
          // Переключиться не вышло (устройство занято другим приложением и т.п.) —
          // остаёмся на текущем потоке, но зовём колбэк: UI должен показать реальный вход.
          onDeviceUnavailableRef.current?.()
        }
      }
    }

    if (aborted()) {
      stream.getTracks().forEach((track) => track.stop())
      startingRef.current = false
      setStarting(false)
      return
    }

    onCaptureStartedRef.current?.(stream.getAudioTracks()[0]?.getSettings().deviceId ?? null)
    streamRef.current = stream

    // countdownSeconds: 0 — старт без отсчёта (диктовка: нажал и сразу говоришь).
    // Через общий путь ниже это дало бы задержку в тик интервала плюс 350 мс,
    // то есть срезало бы начало фразы.
    if (countdownSeconds <= 0) {
      startingRef.current = false
      setStarting(false)
      setCountdownLeft(0)
      beginRecording(stream)
      return
    }

    startingRef.current = false
    setStarting(false)
    setPhase("countdown")
    setCountdownLeft(countdownSeconds)

    let remaining = countdownSeconds
    countdownTimerRef.current = window.setInterval(() => {
      remaining -= 1
      if (remaining > 0) {
        setCountdownLeft(remaining)
        return
      }

      if (countdownTimerRef.current != null) {
        window.clearInterval(countdownTimerRef.current)
        countdownTimerRef.current = null
      }
      setCountdownLeft(0)

      goTimerRef.current = window.setTimeout(() => {
        goTimerRef.current = null
        const activeStream = streamRef.current
        if (!activeStream) {
          startingRef.current = false
          setStarting(false)
          setPhase("idle")
          return
        }
        startingRef.current = false
        setStarting(false)
        beginRecording(activeStream)
      }, 350)
    }, 1000)
  }, [beginRecording, countdownSeconds, phase])

  const stop = useCallback(() => {
    if (phase !== "recording") return
    finishRecording(true)
  }, [finishRecording, phase])

  const cancel = useCallback(() => {
    if (phase === "idle") {
      // Захват ещё запрашивается (права/перечисление устройств): бросаем попытку,
      // иначе поток откроется уже после закрытия формы и запись уйдёт «призраком».
      if (startingRef.current) {
        startTokenRef.current += 1
        startingRef.current = false
        setStarting(false)
      }
      return
    }
    if (phase === "countdown") {
      resetToIdle()
      return
    }
    finishRecording(false)
  }, [finishRecording, phase, resetToIdle])

  useEffect(() => {
    return () => {
      acceptRecordingRef.current = false
      // Незавершённая попытка старта: её поток придёт уже после размонтирования.
      startTokenRef.current += 1
      startingRef.current = false
      const recorder = recorderRef.current
      if (recorder && recorder.state !== "inactive") {
        try {
          recorder.stop()
        } catch {
          // ignore
        }
      }
      clearTimers()
      stopStream()
    }
  }, [clearTimers, stopStream])

  return {
    phase,
    countdownLeft,
    elapsedSeconds,
    stream,
    start: startCountdown,
    stop,
    cancel,
    isBusy: phase !== "idle" || starting,
  }
}
